import { format } from 'date-fns';
import _ from 'lodash';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { ActualityAggregationField } from '../store/actuality.kpi.types';

export type ActualityAggregationFieldDescriptor = {
  field: ActualityAggregationField;
  title: string;
  tags?: string[];
  category?: string;
  formatter?: (v: any) => string;
};

function useActualityAggregationFieldDescriptors(): Record<
  ActualityAggregationField,
  ActualityAggregationFieldDescriptor
> {
  const { t, i18n } = useTranslation('feed');
  const descriptors = useMemo(
    () => [
      {
        field: 'eventHour',
        title: t`Event Hour`,
        tags: [t`Event`, t`Time`, t`Date`, t`Hour`],
        formatter: (v: Date) => (_.isDate(v) ? format(v, 'dd MMM HH:00') : ''),
      },
      {
        field: 'eventDay',
        title: t`Event Day`,
        tags: [t`Event`, t`Time`, t`Date`, t`Day`],
        formatter: (v: Date) => (_.isDate(v) ? format(v, 'dd MMM') : ''),
      },
      {
        field: 'eventWeek',
        title: t`Event Week`,
        tags: [t`Event`, t`Time`, t`Date`, t`Week`],
        formatter: (v: Date) => (_.isDate(v) ? format(v, 'dd MMM') : ''),
      },
      {
        field: 'agentType',
        title: t`Agent Type`,
        tags: [
          t`Event`,
          t`Agent`,
          t`User`,
          t`Labour`,
          t`Employee`,
          t`Resource`,
        ],
      },
      {
        field: 'agentId',
        title: t`Agent Id`,
        tags: [
          t`Event`,
          t`Agent`,
          t`User`,
          t`Labour`,
          t`Employee`,
          t`Resource`,
        ],
      },
      {
        field: 'agentUser',
        title: t`User`,
        tags: [t`Employee`, t`Labour`, t`Resource`, t`User`],
      },

      {
        field: 'eventProcessType',
        title: t`Event Process Type`,
        tags: [t`Event`, t`Process`],
      },
      { field: 'eventType', title: t`Event Type`, tags: [t`Event`, t`Type`] },
      {
        field: 'consignee',
        title: t`Consignee`,
        tags: [t`Event`, t`Item`, t`Client`],
      },
      { field: 'sku', title: t`Item`, tags: [t`Event`, t`Item`] },
      { field: 'uom', title: t`UOM`, tags: [t`Event`, t`Item`] },
      {
        field: 'categoryAbc',
        title: t`ABC Category`,
        tags: [t`Item`, t`KPI`, t`ABC`, t`Velocity`],
      },

      { field: 'jobId', title: t`Job Id`, tags: [t`Process`] },
      {
        field: 'jobGroupId',
        title: t`Order ID`,
        tags: [t`Process`, t`Order`],
      },

      {
        field: 'skuGroup',
        title: t`Item Group`,
        tags: [t`Item`],
        formatter: v => (_.isNil(v) ? t`Unknown` : v),
      },
      { field: 'subGroup', title: t`Sub Group`, tags: [t`Item`] },
      { field: 'transportClass', title: t`Transport Class`, tags: [t`Item`] },
      { field: 'stockCategory', title: t`Stock Category`, tags: [t`Item`] },
      { field: 'storageClass', title: t`Storage Class`, tags: [t`Item`] },
      { field: 'pollutionClass', title: t`Pollution Class`, tags: [t`Item`] },

      {
        field: 'locationStatus',
        title: t`Location Status`,
        tags: [t`Location`],
      },
      { field: 'locationLevel', title: t`Location Level`, tags: [t`Location`] },
      {
        field: 'locationBayTitle',
        title: t`Bay`,
        tags: [t`Location`],
      },
      {
        field: 'locationBayPosition',
        title: t`Bay Position`,
        tags: [t`Location`],
      },
      { field: 'locmhtype', title: t`Location Type`, tags: [t`Location`] },
      {
        field: 'locationRackingType',
        title: t`Location Type`,
        tags: [t`Location`],
      },
      { field: 'locationSide', title: t`Location Side`, tags: [t`Location`] },
      {
        field: 'congestionZone',
        title: t`Work Zone`,
        tags: [t`Location`],
      },
      {
        field: 'locationHeightFromFloor',
        title: t`Location HFF`,
        tags: [t`Location`],
      },
      {
        field: 'locationDepthFromFront',
        title: t`Location Depth From Front`,
        tags: [t`Location`],
      },
      {
        field: 'locationIndexFromFront',
        title: t`Location Index From Front`,
        tags: [t`Location`],
      },
      { field: 'bayType', title: t`Bay Type`, tags: [t`Location`] },
      { field: 'aisleTitle', title: t`Aisle`, tags: [t`Location`] },
      { field: 'planeTitle', title: t`Plane`, tags: [t`Location`] },
      {
        field: 'pickByAssignment',
        title: t`Pick By Assignment`,
        tags: [t`Item`, t`KPI`],
      },
      { field: 'shiftId', title: t`Shift ID`, tags: [t`Shift`] },
      {
        field: 'shiftDay',
        title: t`Shift Day`,
        tags: [t`Shift`, t`Time`],
        formatter: (v: Date) => (_.isDate(v) ? format(v, 'dd MMM') : ''),
      },
    ],
    [i18n.language],
  );

  return _.keyBy(descriptors, 'field') as Record<
    ActualityAggregationField,
    ActualityAggregationFieldDescriptor
  >;
}

export default useActualityAggregationFieldDescriptors;
